import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import "../Styles/CustomCalendar.css";
import { DataContext } from "../Context/Context";
import { eachDayOfInterval, subDays } from "date-fns";
import { generateRandomHashKey } from "../utilities/listingCalendarUtils";

const ListingCalendar = React.memo(({ listingId, handleScrollToTop }) => {
  const {
    setCalender,
    setCurrentDatePrice,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
  } = useContext(DataContext);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [nextMonth, setNextMonth] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)
  );
  const [hoveredDate, setHoveredDate] = useState(null);

  const REFRESH_INTERVAL = 10000; // Poll every 30 seconds for more frequent updates

  const fetchCalendar = useCallback(
    debounce(async () => {
      try {
        setError(null);
        const tokenResponse = await axios.post(
          "https://365luxuryhomes.com/api/accessToken"
        );
        const accessToken = tokenResponse.data.access_token;

        const startDateCal = `${currentMonth.getFullYear()}-${String(
          currentMonth.getMonth() + 1
        ).padStart(2, "0")}-01`;
        const endDateCal = `${nextMonth.getFullYear()}-${String(
          nextMonth.getMonth() + 2
        ).padStart(2, "0")}-01`;

        const response = await axios.get(
          `https://api.hostaway.com/v1/listings/${listingId}/calendar`,
          {
            params: { startDateCal, endDateCal, includeResources: 1 },
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.status === 200) {
          setCalendarData(response.data || []);
          setCalender(response.data || []);
        }
      } catch (error) {
        setError("Failed to fetch calendar data. Please try again.");
      } finally {
        setLoading(false);
      }
    }, 300), // Debounce with 300ms
    [currentMonth, nextMonth, listingId, setCalender]
  );

  useEffect(() => {
    fetchCalendar();
    const intervalId = setInterval(fetchCalendar, REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchCalendar]);

  const formatDate = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCalendarMatrix = useMemo(
    () => (date) => {
      const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      const lastDayOfMonth = new Date(
        date.getFullYear(),
        date.getMonth() + 1,
        0
      );
      const daysInMonth = lastDayOfMonth.getDate();
      const firstWeekday = firstDayOfMonth.getDay();

      const calendarMatrix = [];
      let week = Array(firstWeekday === 0 ? 6 : firstWeekday - 1).fill(null);

      for (let day = 1; day <= daysInMonth; day++) {
        week.push(day);
        if (week.length === 7 || day === daysInMonth) {
          calendarMatrix.push(week);
          week = [];
        }
      }

      // Ensure the last row has 7 days
      if (calendarMatrix[calendarMatrix.length - 1].length < 7) {
        calendarMatrix[calendarMatrix.length - 1] = [
          ...calendarMatrix[calendarMatrix.length - 1],
          ...Array(7 - calendarMatrix[calendarMatrix.length - 1].length).fill(
            null
          ),
        ];
      }

      // Ensure there are always 6 rows
      while (calendarMatrix.length < 6) {
        calendarMatrix.push(Array(7).fill(null));
      }

      return calendarMatrix;
    },
    []
  );

  const getDayStatus = useMemo(
    () => (formattedDate) => {
      const dayData = calendarData?.result?.find(
        (d) => d.date === formattedDate
      );

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const lastAvailableDate = calendarData?.result?.reduce(
        (latest, current) => {
          return new Date(current.date) > new Date(latest.date)
            ? current
            : latest;
        },
        { date: "1970-01-01" }
      ).date;

      if (
        new Date(formattedDate) < today ||
        new Date(formattedDate) > new Date(lastAvailableDate)
      ) {
        return "unavailable";
      }

      if (!dayData) return "available";
      if (dayData.status === "reserved") return "reserved";
      if (!dayData.isAvailable) return "booked";
      return "available";
    },
    [calendarData]
  );

  const getMatchingItems = (calendarData, startDate, endDate) => {
    if (!startDate || !endDate) return [];

    const dateRange = eachDayOfInterval({
      start: new Date(startDate),
      end: subDays(new Date(endDate), 1),
    }).map((date) => formatDate(date));

    const matchingItems = calendarData?.result?.filter((item) =>
      dateRange.includes(item.date)
    );

    return matchingItems;
  };

  useEffect(() => {
    if (startDate && endDate && calendarData) {
      const matchingItems = getMatchingItems(calendarData, startDate, endDate);
      setCurrentDatePrice(matchingItems);
    }
  }, [startDate, endDate, calendarData, setCurrentDatePrice]);

  //Here inDateRange function will called ----------------
  const handleDateClick = (date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else if (startDate && !endDate && date > startDate) {
      const dateRange = eachDayOfInterval({
        start: new Date(startDate),
        end: new Date(date),
      }).map((d) => formatDate(d));

      const hasGap = dateRange.some((d) => {
        const dayStatus = getDayStatus(d);
        return dayStatus === "reserved" || dayStatus === "booked";
      });

      if (!hasGap) {
        setEndDate(date);
      }
    }
  };

  const renderCalendar = (date) => {
    const matrix = getCalendarMatrix(date);

    return (
      <table className="calendar-table border-separate border-spacing-[2px]">
        <thead>
          <tr>
            {["M", "T", "W", "T", "F", "S", "S"].map((day) => (
              <th key={day + generateRandomHashKey()}>{day}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((week, i) => (
            <tr key={i}>
              {week.map((day, idx) => {
                const formattedDate = day
                  ? `${date.getFullYear()}-${String(
                      date.getMonth() + 1
                    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`
                  : null;

                const dayStatus = day ? getDayStatus(formattedDate) : "";
                const currentDate = day
                  ? new Date(date.getFullYear(), date.getMonth(), day)
                  : null;

                const isSelectable = currentDate && dayStatus === "available";

                return (
                  <td
                    key={idx}
                    className={`calendar-cell h-[40px] ${dayStatus} ${
                      isSelectable &&
                      startDate &&
                      !endDate &&
                      hoveredDate &&
                      currentDate >= startDate &&
                      currentDate <= hoveredDate
                        ? "hover-range"
                        : ""
                    } ${!currentDate ? "empty-cell" : ""} ${
                      currentDate?.toDateString() === startDate?.toDateString()
                        ? "selected-start !bg-black"
                        : ""
                    } ${
                      currentDate?.toDateString() === endDate?.toDateString()
                        ? "selected-end !bg-black"
                        : ""
                    } ${
                      startDate &&
                      endDate &&
                      currentDate > startDate &&
                      currentDate < endDate
                        ? "selected-middle"
                        : ""
                    }`}
                    style={{
                      pointerEvents: isSelectable ? "pointer" : "default",
                      opacity: isSelectable ? 1 : 0.5,
                    }}
                    onClick={() => isSelectable && handleDateClick(currentDate)}
                    onMouseEnter={() =>
                      isSelectable &&
                      startDate &&
                      !endDate &&
                      setHoveredDate(currentDate)
                    }
                  >
                    {dayStatus === "booked" ? (
                      <span
                        className="tooltip font-bold line-through !text-[#7f7f7f]"
                        data-tip="Booked"
                      >
                        {day}
                      </span>
                    ) : dayStatus === "reserved" ? (
                      <span
                        className="tooltip line-through"
                        data-tip="Reserved"
                      >
                        {day}
                      </span>
                    ) : dayStatus === "unavailable" ? (
                      <span
                        className="line-through !text-[#7f7f7f]"
                        data-tip=""
                      >
                        {day}
                      </span>
                    ) : (
                      <span
                        className="tooltip font-semibold cursor-pointer hover:border-2 border-black rounded-full w-full h-full flex items-center justify-center"
                        data-tip="Available"
                      >
                        {day || ""}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
    setNextMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
    setNextMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  if (loading) return <p>Loading calendar...</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <>
      <div className="">
        <div className="calendar-header">
          <span className="cursor-pointer" onClick={goToPreviousMonth}>
            &laquo;
          </span>
          <span className="text-[#aca9a5]">
            {currentMonth.toLocaleString("default", {
              month: "long",
              year: "numeric",
            })}
          </span>
          <span className="text-[#aca9a5]">
            {nextMonth.toLocaleString("default", {
              month: "long",
              year: "numeric",
            })}
          </span>
          <span className="cursor-pointer" onClick={goToNextMonth}>
            &raquo;
          </span>
        </div>
        <div className="calendar">
          {renderCalendar(currentMonth)}
          {renderCalendar(nextMonth)}
        </div>
      </div>
      {startDate && endDate && (
        <div
          onClick={handleScrollToTop}
          className="ml-auto px-3 py-1 w-32 rounded-md cursor-pointer border hover:border-[#c3a963] text-center text-sm bg-[#c3a963] text-white hover:bg-gray-100 hover:text-[#c3a963]"
        >
          Select Guests
        </div>
      )}
    </>
  );
});

export default ListingCalendar;

// Utility function for debouncing
function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}
